import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';

export type LazyAutocompleteOption = {
  id: string;
  name: string;
};

type Props = {
  totalCount: number;
  pageSize?: number;
  fetchOptions: (params: { page: number; search: string; pageSize: number }) => Promise<LazyAutocompleteOption[]>;
  label?: string;
  value?: LazyAutocompleteOption | null
  onChange?: (value: LazyAutocompleteOption | null) => void;
};

const LazyAutocomplete: React.FC<Props> = ({
                                             totalCount, value, onChange,
                                             pageSize = 20,
                                             fetchOptions,
                                             label = 'Wybierz opcję'
                                           }) => {
  const [options, setOptions] = useState<LazyAutocompleteOption[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const listboxRef = useRef<HTMLUListElement | null>(null);

  const loadOptions = async () => {
    if (loading) return;
    setLoading(true);
    const newOptions = await fetchOptions({ page, search: inputValue, pageSize });
    setOptions(prev => (page === 1 ? newOptions : [...prev, ...newOptions]));
    setLoading(false);
  };

  useEffect(() => {
    setPage(1); // resetujemy stronę przy zmianie search
  }, [inputValue]);

  useEffect(() => {
    loadOptions();
  }, [page, inputValue]);

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - 1
    ) {
      if (options.length < totalCount) {
        setPage(prev => prev + 1);
      }
    }
  };

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={value}  // <- kontrolowany stan
      onChange={(event, newValue) => {
        onChange?.(newValue); // wywołujemy callback rodzica
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={loading}
      onInputChange={(event, value) => setInputValue(value)}
      ListboxProps={{
        onScroll: handleScroll,
        ref: listboxRef
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />

  );
};

export default LazyAutocomplete;
